import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
  useParams,
  Link,
} from "react-router-dom";
import { toPath, PATHS, PROJECT_PATHS, camelCaseToRegular } from "../../PATHS";
import "./NavBar.scss";
import { IProjectModel } from "../../stores/project/projectModel";
import { observer } from "mobx-react-lite";
import { MSTContext, rootStore } from "../../stores/Root";
import NotificationBellWrapper from "../../components/notification/notificationBell";
import { Button, Layout, Dropdown, Popover, Menu, Tooltip } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { AsyncOpState } from "../../types/enums/async-op-states";
import staticText from "../../utils/staticText";
import { dateFormatter } from "../../helpers/timeAgo.helpers";
import { getENV } from "../../config";
import useDebounce from "../../helpers/DebounceHook";
import {
  BlingsLogo,
  SettingsIcon,
  TimeIcon,
  VideoIcon,
} from "../../assets/Icons";
import { ProjectSearch } from "../ProjectsList/ProjectSearch";
import PlayerManager from "../../utils/playerManager";
import { TextButton } from "../../components/TextButton";
import { IFlowScene } from "@blings/blings-player/lib/src/SDK/sdk.api";
import SceneOrLineupSelect from "../../components/SceneOrLineupSelect";
import { ProjectBasicInfo } from "../../stores/project/projectsModel";
import { STUDIO_BASE_URL } from "../../consts";

type Props = {
  project: IProjectModel | undefined;
  projects: ProjectBasicInfo[];
  operation: string | undefined;
  isPsychedelic: boolean;
  userEmail: string | undefined;
  setSearchView: (searchView: boolean) => void;
  setCurrentSearch: (searchString: string) => void;
  projectCurrentlyLoading: boolean;
  hasUnsavedChanges?: boolean;
  accountCanAi: boolean;
  userHasMultipleAccounts: boolean;
};
export const EXAMPLE_PLAYER_LINK = "https://assets.blings.io/player/index.html";

const NavBarWrapper = observer(() => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  // Get operation from url
  const operation = location.pathname.split("/")?.[3];
  const store = useContext(MSTContext);

  const { projectsStore, editVideoStore } = store;
  const { selectedProject: project, projects } = projectsStore;
  const { hasUnpublishedConnnectorChanges: hasUnsavedChanges } = project || {};
  const projectCurrentlyLoading = store.projectsStore.projectIdCurrentlyLoading;
  const { setSearchView, setCurrentSearch } = editVideoStore;
  const { isPsychedelic } = store.uiStore;
  const userEmail = store.userStore.email;
  const userHasMultipleAccounts = store.userStore.hasMultipleAccounts;
  const canAi =
    (store.userStore.loadStatus === "LOADED" &&
      store.userStore
        .accountFromId(project?.account?.id)
        ?.accountCan("aiOptimization")) ||
    false;
  const [accountCanAi, setAccountCanAi] = useState(canAi);
  useEffect(() => {
    if (store.userStore.loadStatus === "LOADED") {
      setAccountCanAi(
        store.userStore
          .accountFromId(project?.account?.id)
          ?.accountCan("aiOptimization") || false
      );
    }
  }, [store.userStore.loadStatus, project?.account?.id]);

  const state = {
    project: id ? project : undefined,
    hasUnsavedChanges,
    operation,
    projects,
    setSearchView,
    setCurrentSearch,
    isPsychedelic,
    projectCurrentlyLoading: !!projectCurrentlyLoading,
    userEmail,
    accountCanAi,
    userHasMultipleAccounts,
  };
  return <NavBar {...state} />;
});

const NavBar = ({
  project,
  projects: allProjects,
  operation,
  isPsychedelic,
  projectCurrentlyLoading,
  userEmail,
  accountCanAi,
  userHasMultipleAccounts,
}: Props) => {
  const history = useNavigate();

  const minisiteConfig = project?.minisiteConfig;
  const videoPartNames = project?.videoPartNames;
  const selectedScenesFromMinisite = minisiteConfig?.scenes;
  const flowDiagram = project?.flowDiagram;
  const [hiddenNavItems, setHiddenNavItems] = useState<any>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverLiveVersionOpen, setPopoverLiveVersionOpen] = useState(false);

  const navbarRef = useRef<HTMLDivElement>(null);
  const navItemsRef = useRef<HTMLDivElement>(null);

  const [navbarWidth, setNavbarWidth] = useState(0);
  const debouncedNavbarWidth = useDebounce(navbarWidth, 200); // 200ms debounce

  const widthStepsToItemsHide: { [key: number]: number } = {
    1200: 1,
    1100: 2,
    1000: 3,
    940: 4,
    800: 5,
    700: 6,
  };
  useEffect(() => {
    const handleResize = () => {
      if (navbarRef.current) {
        setNavbarWidth(navbarRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    setTimeout(() => {
      handleResize(); // Initial call
    }, 2000);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [projectCurrentlyLoading]); // This effect should only run once on mount/unmount

  useEffect(() => {
    const navItemsChildren = Array.from(
      navItemsRef?.current?.children || []
    ).filter((item: any) => item.id !== "extraOptions");
    const tempHiddenNavItems: any[] = [];
    const tempVisibleNavItems: any[] = [];

    if (navItemsChildren) {
      let itemsToHide = 0;

      // Determine how many items to hide based on the width step.
      for (const width of Object.keys(widthStepsToItemsHide)) {
        if (debouncedNavbarWidth < Number(width)) {
          itemsToHide = widthStepsToItemsHide[Number(width)];
          break;
        }
      }

      Array.from(navItemsChildren).forEach((item, index) => {
        if (index >= navItemsChildren.length - itemsToHide) {
          tempHiddenNavItems.push(item);
        } else {
          tempVisibleNavItems.push(item);
        }
      });

      setHiddenNavItems(tempHiddenNavItems);

      // Handle display for nav items
      tempVisibleNavItems.forEach((item) => (item.style.display = "block"));
      tempHiddenNavItems.forEach((item) => (item.style.display = "none"));
    }
  }, [debouncedNavbarWidth, navItemsRef, popoverOpen]);

  const overLayLogo = [
    {
      key: 0,
      label: (
        <a
          href={
            project?.id
              ? `${STUDIO_BASE_URL}${project?.id}`
              : `${STUDIO_BASE_URL}`
          }
          target="_blank"
        >
          <TextButton underline={false} onClick={() => {}} text={"Studio"} />
        </a>
      ),
    },
    {
      key: 1,
      label: (
        <Link to={toPath(PATHS.data)}>
          <TextButton
            underline={false}
            // outline
            onClick={() => {}}
            text={"Data Uploads"}
          />
        </Link>
      ),
    },
    {
      key: 2,
      label: (
        <Link to={toPath(PATHS.settings)}>
          <TextButton
            // outline
            underline={false}
            onClick={() => {}}
            text={"Account Settings"}
          />
        </Link>
      ),
    },
    {
      key: 3,
      label: (
        <TextButton
          underline={false}
          // outline
          onClick={() => rootStore.userStore.signOut(history)}
          text={"Log Out"}
        />
      ),
    },
  ];
  const [finishedLoadingWatchScene, setFinishedLoadingWatchScene] =
    useState<boolean>(false);

  const [scenes, setScenes] = useState<IFlowScene[]>(() => {
    if (selectedScenesFromMinisite && selectedScenesFromMinisite.length > 0) {
      return selectedScenesFromMinisite.map((scene) => {
        try {
          return JSON.parse(scene);
        } catch (e) {
          return scene;
        }
      });
    }
    if (project?.flowDiagram?.lineups[0])
      return [{ lineup: project.flowDiagram.lineups[0] }];
    if (videoPartNames && videoPartNames.length > 0) return [videoPartNames[0]];
    return [];
  });

  // it takes a while for the scenes to load, so we need to wait for them to load before we can set the selected scenes
  useEffect(() => {
    if (scenes && scenes.length > 0) {
      setFinishedLoadingWatchScene(true);
    }
  }, [scenes]);

  // edge case: if the scenes are not loaded, set the scenes to the first video part name
  // but only if the scenes are not already set
  useEffect(() => {
    if (
      videoPartNames &&
      videoPartNames?.length > 0 &&
      !finishedLoadingWatchScene &&
      scenes?.length === 0
    ) {
      if (flowDiagram && flowDiagram.lineups.length) {
        setScenes([{ lineup: flowDiagram.lineups[0] }]);
        return;
      }
      setScenes([videoPartNames[0]]);
    }
  }, [
    videoPartNames?.length,
    setScenes,
    videoPartNames,
    scenes,
    finishedLoadingWatchScene,
    flowDiagram,
  ]);

  const handleSetScenes = (scenes: IFlowScene[]) => {
    setScenes(scenes);
  };

  useEffect(() => {
    if (project && project.minisiteConfig && project.minisiteConfig.scenes) {
      const scenes =
        project.minisiteConfig.scenes?.map((sceneOrLineup) => {
          try {
            return JSON.parse(sceneOrLineup);
          } catch (e) {
            return sceneOrLineup;
          }
        }) || [];
      setScenes(scenes);
    }
  }, [project]);

  const [publishButtonState, setPublishButtonState] = useState<AsyncOpState>(
    AsyncOpState.Changed
  );
  const publishButtonTexts = {
    [AsyncOpState.Changed]: "Publish",
    [AsyncOpState.Saving]: "Publishing",
    [AsyncOpState.Success]: "Published",
    [AsyncOpState.Error]: "Error",
  };
  const lastPublishedAt = project?.publishedAt || project?.updatedAt || "";

  /**
   *  Check if project has nav item to render in the navbar
   * @param navItem
   * @returns
   */
  const projectHasNavItem = (navItem: string) => {
    switch (navItem) {
      case PROJECT_PATHS.customAnalytics:
        return !!project?.analyticsReportUrl;
      case PROJECT_PATHS.analytics:
        return project?.analyticsEnabled && !project.analyticsReportUrl;
      case PROJECT_PATHS.liveControl:
        return false;
      case PROJECT_PATHS.formData:
        if (!project) return false;
        return project.hasFormData;
      case PROJECT_PATHS.optimization:
        return accountCanAi;
      case PROJECT_PATHS.flowMap:
        return userEmail?.endsWith("@blings.io") || false;
      case PROJECT_PATHS.deepAnalytics:
        // Snowflake Streamlit embed — hidden until the feature is ready.
        return false;
      default:
        return true;
    }
  };

  const getMenuButtonLink = (project: IProjectModel, navItem: any) => {
    if (navItem[0] === "editContent" && project?.instantiatedFromUseCase) {
      return toPath(PATHS.project, project.id, navItem[1]);
    }
    return toPath(PATHS.project, project.id, navItem[1]);
  };

  function openLiveVersion() {
    let url = `${EXAMPLE_PLAYER_LINK}?env=${getENV()}&p=${project?.id || ""}`;
    if (scenes) {
      url += `&scenes=${encodeURIComponent(
        scenes
          .map((scene) =>
            typeof scene === "string" ? scene : JSON.stringify(scene)
          )
          .join()
      )}`;
    }
    const publishedExampleData = project?.stateJsonSchemaStr
      ? JSON.parse(project.stateJsonSchemaStr).examples[0]
      : null;
    if (publishedExampleData) {
      url += `&data=${encodeURIComponent(
        JSON.stringify(publishedExampleData)
      )}`;
    }

    const currentVersion = PlayerManager.get().GetCurrentPlayerVersion();
    // Do nothing if the player has the latest version
    if (currentVersion === "latest") {
    }
    // If there is no player version, use the one that the project has specified
    else if (currentVersion === "WARN_NO_PLAYER_VERSION") {
      console.warn("No player version specified, using project version");
      console.warn("Project version: ", project?.playerVersionToUse);
      url += `&v=${(project?.playerVersionToUse as string).replaceAll(
        ".",
        "-"
      )}`;
    }
    // If there is a player version, use that
    else {
      url += `&v=${currentVersion}`;
    }

    window.open(url, "_blank");
  }
  const Title = (title: string, text: string) => {
    const validTitle = projectCurrentlyLoading
      ? "Loading Project..."
      : title || "Loading Project...";
    return (
      <div className="title-container">
        <div className="title-wrapper">
          <span className="title">{validTitle}</span>
          <span className="sub-title">{text}</span>
        </div>
      </div>
    );
  };
  const getPageTitle = () => {
    switch (operation) {
      case PROJECT_PATHS.editContent:
        return Title(
          "Edit Content",
          "Improve your content globally for all viewers"
        );
      case PROJECT_PATHS.overview:
        return Title(
          project?.title || "",
          "Overview of your project's essentials"
        );
      case PROJECT_PATHS.settings:
        return Title(
          "Settings",
          "Adjust project details, player options, and MP5 data structure"
        );
      case PROJECT_PATHS.integration:
        return Title(
          "Integration",
          "Integrate MP5 videos into your CRM and custom HTML pages"
        );
      case PROJECT_PATHS.analytics:
        return Title(
          "Analytics",
          "View insights on MP5 performance & audience behavior"
        );
      case PROJECT_PATHS.formData:
        return Title(
          "Form Data",
          "Review and export insights from your viewers"
        );
      case PROJECT_PATHS.optimization:
        return Title("AI Optimization", "Improve your MP5 results with AI");
      case PROJECT_PATHS.flowMap:
        return Title("Flow Map", "Manage your Flow");
      case PROJECT_PATHS.deepAnalytics:
        return Title(
          "Deep Analytics",
          "Explore detailed project analytics powered by Snowflake"
        );
      default:
        return "";
    }
  };

  return (
    <>
      <Layout>
        <Layout.Header className={"Main Header"}>
          <div className={"NavBar"} ref={navbarRef}>
            <div className="left">
              <Link to={"/"} className="LogoLink">
                <div
                  style={{
                    color: isPsychedelic ? "orange" : "#ff3666",
                    display: "flex",
                  }}
                >
                  <BlingsLogo />
                </div>
              </Link>
              {project ? (
                <>
                  <ProjectSearch
                    projectCurrentlyLoading={projectCurrentlyLoading}
                    selectedProject={project}
                    projects={allProjects}
                    userHasMultipleAccounts={userHasMultipleAccounts}
                  />
                  <div className="menu-items" ref={navItemsRef}>
                    {Object.entries(PROJECT_PATHS).map(
                      (navItem) =>
                        projectHasNavItem(navItem[1]) && (
                          <NavLink
                            key={toPath(navItem[1])}
                            id={camelCaseToRegular(navItem[0])}
                            to={getMenuButtonLink(project, navItem)}
                            className={({ isActive }) =>
                              [
                                "NavItem",
                                isActive
                                  ? // ||
                                    // (operation === "edit-video" &&
                                    //   navItem[1] === "edit-video")
                                    "Selected"
                                  : "",
                              ].join(" ")
                            }
                          >
                            {camelCaseToRegular(navItem[0])}
                          </NavLink>
                        )
                    )}
                    {hiddenNavItems.length ? (
                      <Dropdown
                        menu={{
                          items: hiddenNavItems.map((item: any) => ({
                            key: item.id,
                            label: (
                              <NavLink to={item.getAttribute("href")}>
                                {item.textContent}
                              </NavLink>
                            ),
                          })),
                        }}
                      >
                        <div
                          id="extraOptions"
                          style={{ fontSize: "20px", cursor: "pointer" }}
                        >
                          ...
                        </div>
                      </Dropdown>
                    ) : null}
                  </div>
                </>
              ) : (
                <div></div>
              )}
            </div>
            <div className="RightNav">
              {project ? (
                <>
                  <div
                    id="ExtraInfo-long"
                    title={new Date(lastPublishedAt).toString()}
                  >
                    {staticText.header.VERSION(project.isLiveVersion)}{" "}
                    {staticText.header.LAST_EDIT}
                    {lastPublishedAt
                      ? dateFormatter(new Date(lastPublishedAt))
                      : "done"}{" "}
                    {/* {staticText.header.BY_USER("User")} */}
                  </div>
                  <div id="ExtraInfo-short">
                    <Tooltip
                      placement="bottomLeft"
                      title={new Date(lastPublishedAt).toString()}
                    >
                      <div style={{ display: "flex" }}>
                        <TimeIcon />
                      </div>
                    </Tooltip>
                  </div>

                  <div id="PreviewButton" className="previewPublished-long">
                    <Popover
                      overlayClassName="view-live-version-popover"
                      placement="bottomRight"
                      title="Pick a Flow:"
                      open={popoverLiveVersionOpen}
                      onOpenChange={setPopoverLiveVersionOpen}
                      content={
                        <div>
                          <SceneOrLineupSelect
                            flowDiagram={flowDiagram}
                            scenes={videoPartNames || []}
                            value={scenes}
                            onChange={setScenes}
                            className="view-live-version-select"
                          />
                          <Button type="primary" onClick={openLiveVersion}>
                            Watch
                          </Button>
                        </div>
                      }
                      trigger="click"
                    >
                      <div className="previewPublished-long">
                        <Tooltip
                          title="Watch Published Version"
                          placement="bottom"
                        >
                          <Button className="preview" type="text">
                            <VideoIcon />
                          </Button>
                        </Tooltip>
                      </div>
                      <div className="previewPublished-short">
                        <Tooltip
                          title="Watch Published Version"
                          placement="bottom"
                        >
                          <Button className="preview" type="text">
                            <VideoIcon />
                          </Button>
                        </Tooltip>
                      </div>
                    </Popover>
                  </div>

                  <div className="notificationIcon">
                    <NotificationBellWrapper />
                  </div>
                  <Dropdown menu={{ items: overLayLogo }} trigger={["click"]}>
                    <div className="settings">
                      <SettingsIcon />
                    </div>
                  </Dropdown>
                </>
              ) : (
                <>
                  <div className="user-email">{userEmail}</div>
                  <div className="notificationIcon">
                    <NotificationBellWrapper />
                  </div>
                  <Dropdown menu={{ items: overLayLogo }} trigger={["click"]}>
                    <div className="settings" id="account-dropdown">
                      <SettingsIcon />
                    </div>
                  </Dropdown>
                </>
              )}
            </div>
          </div>
        </Layout.Header>
        <Layout.Content className={"Main-Content"}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: getPageTitle() === "" ? "" : "100%",
              overflow: "hidden",
            }}
          >
            {getPageTitle()}

            <Outlet />
          </div>
        </Layout.Content>
      </Layout>
    </>
  );
};

export default NavBarWrapper;
